import cn from 'classnames'
import {useInView} from 'framer-motion'
import type {AnimatedListSliceMap} from 'prismicio-types'
import {useCallback, useEffect, useRef, useState} from 'react'
import {Container} from 'src/common/Container'
import {MapSvg} from './components/MapSvg'

type MapVariationProps = {
  slice: AnimatedListSliceMap & {
    slice_type: string
    variation: string
  }
  pageUid?: string
  componentIndex?: number
}

export const MapVariation = ({slice, pageUid: _pageUid, componentIndex: _componentIndex}: MapVariationProps) => {
  const [activeIndex, setActiveIndex] = useState(0)
  const intervalRef = useRef<NodeJS.Timeout | null>(null)
  const containerRef = useRef(null)
  const isInView = useInView(containerRef, {amount: 0.5, once: false})

  // Array of st1 elements for cycling through
  const st1Elements = ['st1-element-3', 'st1-element-1', 'st1-element-2', 'st1-element-0', 'st1-element-4']

  // Start animation when the component is in view
  const startInterval = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
    }

    intervalRef.current = setInterval(() => {
      setActiveIndex((prev) => {
        if (prev + 1 < st1Elements.length) {
          return prev + 1
        } else {
          return 0
        }
      })
    }, 5000)
  }, [st1Elements.length])

  // Start/stop interval based on component visibility
  useEffect(() => {
    if (!isInView) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }

      return
    }

    startInterval()

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
    }
  }, [isInView, startInterval])

  const handleItemClick = (index: number) => {
    setActiveIndex(index)
    startInterval() // Restart the interval
  }

  return (
    <Container
      backgroundColor="Moss White"
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="px-5 md:px-10 lg:px-5 xl:px-0"
      containerRef={containerRef}
    >
      <div
        className="relative isolate flex h-[640px] w-full max-w-[1200px] flex-col overflow-hidden rounded-lg bg-green-dark p-6 lg:h-[648px] lg:p-12"
        data-aos="fade-up"
        data-aos-offset="0"
      >
        <div className="absolute bottom-0 left-0 z-10 h-[400px] w-full bg-[linear-gradient(0deg,rgba(40,24,10,0.40)_65.52%,rgba(40,24,10,0)_89.33%)] backdrop-blur-0 md:block md:h-[200px] lg:hidden" />
        <div className="absolute inset-0 z-10 h-[60px] w-full bg-[linear-gradient(180deg,rgba(40,24,10,0.40)_25.52%,rgba(40,24,10,0)_75.33%)] backdrop-blur-0 md:block lg:bottom-auto lg:left-auto lg:right-0 lg:top-0 lg:block lg:h-full lg:w-[80px] lg:bg-[linear-gradient(270deg,#002414_19.23%,rgba(0,36,20,0)_100%)]" />
        <div className="absolute right-0 top-0 z-10 h-full w-20 bg-[linear-gradient(270deg,rgba(40,24,10,0.40)_25.52%,rgba(40,24,10,0)_75.33%)] backdrop-blur-0 lg:bottom-auto lg:left-auto lg:right-[-100px] lg:top-0 lg:z-0 lg:block lg:h-full lg:w-[300px] lg:bg-[linear-gradient(270deg,#002414_19.23%,rgba(0,36,20,0)_100%)]" />
        <MapSvg activeIndex={activeIndex} setActiveIndex={setActiveIndex} elements={slice.primary.elements} />
        <div className="flex h-full flex-col justify-between">
          <h3 className="text-tg-h3 z-10 text-moss-white">{slice.primary.title}</h3>
          <ul className="z-10 flex flex-col gap-3">
            {slice.primary.elements.map((element, index) => (
              <li key={index}>
                <h3
                  className={cn(
                    'w-full cursor-pointer text-[clamp(48px,5vw,64px)] font-normal leading-12 tracking-[-.48px] transition-colors duration-300 hover:text-moss-grey-150 hover:!delay-0 hover:!duration-200 lg:w-[70%] lg:max-w-[835px] lg:leading-[64px] lg:tracking-[-.64px]',
                    {
                      'text-moss-white': activeIndex === index,
                      'text-moss-grey-200': activeIndex !== index,
                    },
                  )}
                  onClick={() => handleItemClick(index)}
                >
                  {element.text}
                </h3>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </Container>
  )
}
